import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import TariffBandsValidator from "../validators/TariffBandsValidator";

import { useTariffBands, useAddTariffBand, useUpdateTariffBand, useDeleteTariffBand } from "../../../services/tariff";
import {
  MaskedNumber,
} from "../../../controls";
import {defaultComponents} from '../../../controls/Material-Table-Components';


const TariffBandList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(TariffBandsValidator, true);

  const props = {
    ..._props
  }
  const classes = props.classes;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const tariffLineUuid = props.tariffLineUuid;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddTariffBand = useAddTariffBand();
  const handleUpdateTariffBand = useUpdateTariffBand();
  const handleDeleteTariffBand = useDeleteTariffBand();

  const queryClient = useQueryClient();
  const extraKeys = [tariffLineUuid];

  const tariffBands = useTariffBands({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {textAlign: 'right'}, {textAlign: 'right'}, undefined, false),
        title: "From",
        field: "bandMinimum",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(9)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.bandMinimum}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {textAlign: 'right'}, {textAlign: 'right'}, undefined, false),
        title: "To",
        field: "bandMaximum",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(9)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.bandMaximum}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {textAlign: 'right'}, {textAlign: 'right'}, undefined, false),
        title: "Rate",
        field: "rate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.rate}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(30, undefined, undefined, {textAlign: 'right'}, {textAlign: 'right'}, undefined, false),
        title: "",
      },
      {
        ...baseColumn,
        title: "",
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{marginLeft: 50, width: "40%"}} className={state.canEdit?classes.tariffBandListEdit:classes.tariffBandList}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          headerRowStyle: {
            display: "table",
            width: "105.5%",
          },
          showTitle: true,
          search: false,
          toolbarButtonAlignment: "right",
          padding: "dense",
          paging: false,
          // minBodyHeight: "300", //168+60//FIXME to calculate dynamic height, needed for correct scroll position identification
          // maxBodyHeight: "200"
          addRowPosition: "first"
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title="Rate Bands"
        columns={getColumns({canEdit: state.canEdit})}
        data={tariffBands.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                handleAddTariffBand.mutate({record: {...newData, tariffLineUuid}, resolve, reject, extraKeys});
              })
              : undefined,
          onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateTariffBand.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
              }),
          onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteTariffBand.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
        }}
      />
    </div>
  );

}

export default TariffBandList;
